/**
 * Typed inbox threads (Asana 1219952262403623).
 *
 * One conversation per subject: a trade, a case, a trade offer, the direct
 * line to one person, or the per-user system thread. Field names follow the
 * ticket's Backend Contract block plus the review additions of 2026-10-28
 * (server group order with groupCounts, inbox counts endpoint, caseNumber as
 * an integer formatted client-side, finish/id/removedAt/completedAt on items,
 * CANCELLED and offer event kinds, no delivery event, trackingNumber always
 * null). Everything new on an existing response is optional so a stale cache
 * or an older backend renders as a DIRECT thread in Earlier instead of failing.
 */

import type { HaveFinish } from 'utils/finishes';
import type { MessageImage } from './message';

/**
 * CASE is legacy only (change 2, 2026-10-28): a case is now a phase of its
 * trade's TRADE thread. Threads created before that still answer as CASE and
 * keep rendering with CaseThreadContext.
 */
export type ThreadSubjectType = 'DIRECT' | 'TRADE' | 'CASE' | 'OFFER' | 'SYSTEM';

/** Case phase rows inside a trade thread (Message.kind). */
export type ThreadMessageKind = 'ADMIN_SUMMONED' | 'CASE_RESOLVED' | 'CASE_REOPENED' | 'ADMIN_JOINED';

export type SenderRole = 'USER' | 'ADMIN' | 'SYSTEM';

export type ThreadGroup = 'NEEDS_YOU' | 'ACTIVE' | 'EARLIER';

export type ThreadStatusTone = 'blue' | 'orange' | 'green' | 'grey' | 'red' | 'purple';

export interface ThreadStatusChip {
  label: string;
  tone: ThreadStatusTone;
}

/** Query value of the inbox `type` param; `all` sends no filter. */
export type ThreadTypeFilter = 'all' | 'trade' | 'offer' | 'case' | 'direct' | 'system';

/** Unread counts per type, non-archived (GET /messages/inbox/counts). */
export interface InboxCounts {
  all: number;
  trade: number;
  offer: number;
  case: number;
  direct: number;
  system: number;
}

/** Row counts per group over the filtered, non-archived set (not per page). */
export interface InboxGroupCounts {
  needsYou: number;
  active: number;
  earlier: number;
}

/**
 * Lifecycle events derived at read time from trade and offer timestamps
 * (backend plan comment, EventKind). Nothing records a delivery, so the trade
 * lifecycle is created, shipped, completed; the renderer skips kinds it does
 * not know.
 */
export type ThreadEventKind =
  | 'CREATED'
  | 'SHIPPED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'PROPOSED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'EXPIRED'
  | 'FAILED'
  | ThreadMessageKind;

export interface ThreadEvent {
  kind: ThreadEventKind;
  at: string;
  /** Set on case phase events: the message row the event came from. */
  messageId?: string;
}

export type TradeRole = 'SENDER' | 'RECEIVER';

export interface ThreadTradeItem {
  id: string;
  cardId?: number;
  scryfallId?: string;
  name: string;
  setCode: string;
  setName?: string;
  collectorNumber: string;
  condition: string;
  language: string;
  foil: boolean;
  /** Authoritative finish; `foil` is its mirror. Absent only on an older backend. */
  finish?: HaveFinish | null;
  quantity: number;
  pointValue: number;
  /** Per-card settlement markers (admin per-card Complete and Cancel). */
  completedAt?: string | null;
  removedAt?: string | null;
  imageUrl?: string | null;
}

/**
 * The trade's case, rendered inside the trade thread (change 2). Everything
 * past status is optional so the previous backend shape still types.
 */
export interface ThreadCaseSummary {
  caseId: string;
  /** Raw integer; render with formatCaseNumber. */
  caseNumber: number;
  status: string;
  /** The thread this case lives in: the trade thread itself since change 2. */
  conversationId: string;
  reason?: CaseReason | null;
  openedBy?: { username: string; role: TradeRole };
  alsoRequestedByUsername?: string | null;
  dueAt?: string | null;
  affectedItemIds?: string[];
  description?: string | null;
  /** Moves on every reopen; the admin page collapses what came before. */
  dividerTimestamp?: string;
}

export interface TradeThreadContext {
  tradeId: string;
  tradeNumber: string;
  role: TradeRole;
  status: string;
  createdAt: string;
  shippedAt: string | null;
  /** Also set on cancel; read `status` to tell the two apart. */
  completedAt?: string | null;
  /** Server-formatted label, rendered verbatim. */
  estimatedArrivalLabel: string | null;
  /** Always null today (no tracking integration); rendered only when present. */
  trackingNumber: string | null;
  totalValue: number;
  items: ThreadTradeItem[];
  caseSummary: ThreadCaseSummary | null;
}

export type CaseReason =
  | 'SENDER_PARTNER_NOT_COMPLETED'
  | 'SENDER_CANCEL'
  | 'RECEIVER_NOT_ARRIVED'
  | 'RECEIVER_ARRIVED_INCORRECT'
  | 'RECEIVER_CANCEL'
  | 'OTHER';

export type CaseSource = 'SENDING' | 'RECEIVING' | 'INBOX';

export interface CaseThreadContext {
  caseId: string;
  caseNumber: number;
  status: string;
  reason: CaseReason | null;
  summonedByUsername: string;
  summonedByRole: TradeRole;
  alsoRequestedByUsername?: string | null;
  dueDate: string | null;
  tradeId: string;
  tradeNumber: string;
  tradeStatus?: string;
  /** Viewer's role on the underlying trade, for the link to the trade page. */
  role: TradeRole;
  totalValue: number;
  affectedItemIds: string[];
  description?: string | null;
  images?: MessageImage[];
  items: ThreadTradeItem[];
  dividerTimestamp?: string;
}

export interface OfferThreadSide {
  count: number;
  points: number;
}

export interface OfferThreadContext {
  offerId: string;
  status: string;
  proposedAt: string;
  resolvedAt: string | null;
  give: OfferThreadSide;
  get: OfferThreadSide;
  createdTradeNumbers: string[];
  counterpartyUsername?: string;
}

export type ThreadContext = TradeThreadContext | CaseThreadContext | OfferThreadContext | null;

/** Fields the inbox list row carries on top of the legacy Conversation shape. */
export interface ThreadRowFields {
  subjectType?: ThreadSubjectType;
  subjectId?: string | null;
  /** e.g. "Trade 26-3834 · Reflecting Pool"; absent on legacy rows. */
  subjectTitle?: string | null;
  /** e.g. "Sending · Shipped 10.08". */
  subjectRef?: string | null;
  status?: ThreadStatusChip | null;
  group?: ThreadGroup;
}

/** Fields the thread response carries on top of the legacy ConversationThread shape. */
export interface ThreadDetailFields {
  subjectType?: ThreadSubjectType;
  subjectId?: string | null;
  events?: ThreadEvent[];
  context?: ThreadContext;
}

export const isTradeContext = (c: ThreadContext | undefined): c is TradeThreadContext =>
  !!c && 'tradeId' in c && !('caseId' in c) && !('offerId' in c);

export const isCaseContext = (c: ThreadContext | undefined): c is CaseThreadContext =>
  !!c && 'caseId' in c && 'affectedItemIds' in c;

export const isOfferContext = (c: ThreadContext | undefined): c is OfferThreadContext =>
  !!c && 'offerId' in c;
